import type { DocumentTypeDecoration } from '@graphql-typed-document-node/core';
import { ClientError, GraphQLClient, type RequestOptions } from 'graphql-request';

import { KeyCaseType, transformCaseKeys } from '../shared/helpers';
import { RequestAbortError } from './RequestAbortError';
import { BaseUrlProvider, baseUrlProvider as defaultBaseUrlProvider } from './baseUrlProvider';
import { performRestRequest, RestError, type RestResponseType } from './restRequest';
import { GatewayName } from './types';

export type TokenGetter = () => Promise<string>;

type RequestHeaders = Record<string, string | undefined>;

type RequestParams = Record<string, unknown>;

type RequestData = BodyInit | Record<string, unknown> | unknown[];
export { RestError } from './restRequest';
export type { GatewayResponse, RestErrorResponseData } from './restRequest';

export type FullRequestOptions = {
  baseURL?: string;
  data?: RequestData;
  headers?: RequestHeaders;
  method?: string;
  params?: RequestParams;
  responseType?: RestResponseType;
  signal?: AbortSignal;
  url?: string;
  validStatuses?: readonly number[];
};

// only for use with GET requests
// if mutation requests need options in the future, use a MutationRequestOptions type
export type QueryRequestOptions = Pick<FullRequestOptions, 'signal'>;

const METHODS_WITH_DEFAULT_JSON_BODY = new Set(['POST', 'PUT', 'PATCH']);

function isTransformableData(value: unknown): value is Record<string, unknown> | unknown[] {
  return (
    value != null &&
    typeof value === 'object' &&
    !(value instanceof Blob) &&
    !(value instanceof FormData) &&
    !(value instanceof URLSearchParams) &&
    !(value instanceof ArrayBuffer) &&
    !ArrayBuffer.isView(value)
  );
}

function transformResponseData<T>(data: T): T {
  return isTransformableData(data)
    ? (transformCaseKeys(
        data as Record<string, unknown> | Record<string, unknown>[],
        KeyCaseType.CAMEL,
      ) as T)
    : data;
}

function transformRequestData(data?: RequestData) {
  return isTransformableData(data)
    ? (transformCaseKeys(
        data as Record<string, unknown> | Record<string, unknown>[],
        KeyCaseType.SNAKE,
      ) as RequestData)
    : data;
}

function isAbortError(error: unknown, signal?: RequestOptions['signal']) {
  if (error instanceof ClientError) {
    return false;
  }

  return (
    signal?.aborted === true ||
    (typeof DOMException !== 'undefined' &&
      error instanceof DOMException &&
      error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

export abstract class BaseGateway {
  protected abstract name: GatewayName;
  protected graphqlClient: GraphQLClient;
  protected getAccessToken?: TokenGetter;
  protected baseUrlProvider: BaseUrlProvider;

  constructor(
    getAccessToken?: TokenGetter,
    baseUrlProvider: BaseUrlProvider = defaultBaseUrlProvider,
  ) {
    this.getAccessToken = getAccessToken;
    this.baseUrlProvider = baseUrlProvider;

    const graphqlOrigin = baseUrlProvider.getOrigin();
    const graphqlPath = baseUrlProvider.getForGateway('graphql');
    const graphqlBaseUrl = new URL(graphqlPath, graphqlOrigin).toString();
    this.graphqlClient = new GraphQLClient(graphqlBaseUrl, { errorPolicy: 'none' });
  }

  protected httpGet<T = any>(url: string, options?: Partial<FullRequestOptions>) {
    return this.httpRequest<T>({ ...options, url, method: 'get' });
  }

  protected httpPost<T = any>(url: string, options?: Partial<FullRequestOptions>) {
    return this.httpRequest<T>({ ...options, url, method: 'post' });
  }

  protected httpDelete<T = any>(url: string, options?: Partial<FullRequestOptions>) {
    return this.httpRequest<T>({ ...options, url, method: 'delete' });
  }

  protected httpPatch<T = any>(url: string, options?: Partial<FullRequestOptions>) {
    return this.httpRequest<T>({ ...options, url, method: 'patch' });
  }

  protected httpPut<T = any>(url: string, options?: Partial<FullRequestOptions>) {
    return this.httpRequest<T>({ ...options, url, method: 'put' });
  }

  async graphqlQuery<
    QueryResult = Record<string, any>,
    Variables extends Record<string, any> = Record<string, any>,
  >(
    query: DocumentTypeDecoration<QueryResult, Variables>,
    variables?: Variables,
    signal?: RequestOptions['signal'],
  ): Promise<QueryResult> {
    if (signal?.aborted) {
      throw new RequestAbortError();
    }

    const accessToken = this.getAccessToken ? await this.getAccessToken() : null;

    if (signal?.aborted) {
      throw new RequestAbortError();
    }

    // graphql-request's RequestOptions uses deep conditional types to decide
    // if `variables` is required. We'd normally use RequestOptions<Variables>,
    // but replicating that logic here is painful and unnecessary — the
    // DocumentTypeDecoration<QueryResult, Variables> generic already guarantees
    // `variables` is correct. This cast is safe.
    const opts: RequestOptions = {
      document: query.toString(),
      variables,
      requestHeaders: {
        ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
      },
      signal,
    };

    try {
      return await this.graphqlClient.request<QueryResult>(opts);
    } catch (error) {
      if (isAbortError(error, signal)) {
        throw new RequestAbortError({ cause: error });
      }

      throw error;
    }
  }

  protected async httpRequest<T = any>({
    baseURL,
    data,
    headers,
    method,
    params,
    responseType = 'json',
    signal,
    url,
    validStatuses,
  }: FullRequestOptions) {
    const accessToken = this.getAccessToken ? await this.getAccessToken() : undefined;
    const normalizedMethod = method?.toUpperCase();
    const normalizedData =
      data === undefined && normalizedMethod && METHODS_WITH_DEFAULT_JSON_BODY.has(normalizedMethod)
        ? {}
        : data;
    const requestHeaders = Object.fromEntries(
      Object.entries({
        'Content-Type': 'application/json',
        authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        ...headers,
      }).filter(([, value]) => value !== undefined),
    ) as Record<string, string>;

    try {
      const response = await performRestRequest<T>({
        baseURL: baseURL || this.baseUrlProvider.getForGateway(this.name),
        data: transformRequestData(normalizedData),
        headers: requestHeaders,
        method: normalizedMethod,
        params: params
          ? (transformCaseKeys(params, KeyCaseType.SNAKE) as Record<string, unknown>)
          : undefined,
        responseType,
        signal,
        url: url || '',
        validStatuses,
      });

      return {
        ...response,
        data: transformResponseData(response.data),
      };
    } catch (error) {
      if (error instanceof RestError) {
        error.response.data = transformResponseData(error.response.data);
      }

      throw error;
    }
  }
}
